import { useContext, useState } from 'react';
import { Badge, Button, Icon } from '@aidkitorg/component-library';
import { ApplicantContext } from '../Contexts/ApplicantContext';
import { snakeToEnglish, copyToClipboard } from '../Util';

interface ApplicantHeaderCardProps {
  pinnedApplicantInfo: string[] | undefined;
  applicationState?: {
    field: string,
    colors: {
      stateValue: string,
      color: 'blue' | 'green' | 'red' | 'yellow'
    }[]
  }
  uid: string | undefined;
  isCollapsed: boolean;
  onCollapseChange: (collapsed: boolean) => void;
}

export function ApplicantHeaderCard({ pinnedApplicantInfo, applicationState, uid, isCollapsed, onCollapseChange }: ApplicantHeaderCardProps) {
  const { info } = useContext(ApplicantContext);
  const [copiedUID, setCopiedUID] = useState(false);

  const handleCopyUID = async () => {
    if (uid) {
      await copyToClipboard(uid);
      setCopiedUID(true);
      setTimeout(() => setCopiedUID(false), 2000);
    }
  };

  const getBadgeVariant = (stateValue: string | undefined): 'info' | 'success' | 'error' | 'warning' | 'neutral' => {
    if (!stateValue) return 'neutral';

    // Normalize the state value to handle snake_case configurations
    const normalizedStateValue = snakeToEnglish(stateValue);

    // Check if there's a configured color for this application state
    // Compare both the normalized version and the original value to support both formats
    const colorConfig = applicationState?.colors?.find(
      config => {
        const normalizedConfigValue = snakeToEnglish(config.stateValue);
        return normalizedConfigValue === normalizedStateValue
      }
    );

    if (colorConfig) {
      // Map color to badge variant
      switch (colorConfig.color) {
        case 'blue':
          return 'info';
        case 'green':
          return 'success';
        case 'red':
          return 'error';
        case 'yellow':
          return 'warning';
        default:
          return 'neutral';
      }
    }

    // No color configuration found, return neutral
    return 'neutral';
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Get the application state field name and value
  const applicationStateField = applicationState?.field;
  const applicationStateValue = info[applicationStateField as keyof typeof info] as string | undefined;
  const displayStateValue = applicationStateValue ? snakeToEnglish(applicationStateValue) : undefined;

  // Check if there are any valid pinned fields to display (excluding the application state field)
  const hasCollapsibleContent = pinnedApplicantInfo && pinnedApplicantInfo.length > 0 &&
    pinnedApplicantInfo.filter(fieldName => ![applicationStateField].includes(fieldName)).some(fieldName =>
      info[fieldName as keyof typeof info]
    );

  return (
    <div className="sticky top-0 z-10 border-b bg-white">
      <div className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-2xl font-semibold mb-0">
            {info.legal_name}
          </h2>
          <div className="flex gap-3">
            {applicationStateValue && (
              <Badge variant={getBadgeVariant(applicationStateValue)}>
                {displayStateValue}
              </Badge>
            )}
            {hasCollapsibleContent && (
              <Button
                variant="unstyled"
                onClick={() => onCollapseChange(!isCollapsed)}
                className="p-2 rounded-md hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
                aria-label={isCollapsed ? "Expand applicant details" : "Collapse applicant details"}
              >
                <Icon
                  name={isCollapsed ? "ChevronDownIcon" : "ChevronUpIcon"}
                  type="solid"
                  size="sm"
                  role="decorative"
                />
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-2 items-center text-sm">
          {uid && (
            <div className="flex items-center gap-1.5">
              <span className="text-gray-500">UID: </span>
              <span className="break-all">{uid}</span>
              <Button
                icon={<Icon role="decorative" name={copiedUID ? "CheckIcon" : "ClipboardDocumentIcon"} />}
                variant="secondary"
                size="sm"
                aria-label="Copy UID to clipboard"
                onClick={handleCopyUID}
              />
            </div>
          )}
        </div>

        {/* Collapsible section with pinned fields */}
        {!isCollapsed && (
          <div className="pt-3 grid grid-cols-2 md:grid-cols-4 text-sm gap-4">
            {pinnedApplicantInfo?.filter(fieldName => ![applicationStateField].includes(fieldName)).map((fieldName) => {
              // check that fieldName from pinnedApplicantInfo is available in Applicant Info
              const fieldValue = info[fieldName as keyof typeof info];
              if (!fieldValue) return null;

              // Check if this is a date field that should be formatted
              const isDateField = fieldName.endsWith('_date') || fieldName.endsWith('_created_at') || fieldName.endsWith('_updated_at');

              return (
                <div key={fieldName}>
                  <div className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">
                    {snakeToEnglish(fieldName)}
                  </div>
                  {fieldName === 'birth_date' && info.age ? (
                    <div className="text-sm capitalize break-all">
                      {formatDate(String(fieldValue))} <span className="ps-1">({info.age})</span>
                    </div>
                  ) : isDateField ? (
                    <div className="text-sm capitalize break-all">{formatDate(String(fieldValue))}</div>
                  ) : (
                    <div className="text-sm capitalize break-all">{String(fieldValue)}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
